import { CurrencyPipe, DecimalPipe } from '@angular/common';
import { Component, DestroyRef, OnInit, computed, effect, inject, signal } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidatorFn,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogClose,
  MatDialogRef,
} from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import {
  RunTransition,
  RunTransitionWithDialog,
  WorkflowService,
} from 'src/app/shared/services/workflow/workflow.service';
import { PayeeButtonComponent } from '../../payee-buttons/payee-button/payee-button.component';
import { PayeeIconButtonComponent } from '../../payee-buttons/payee-icon-button/payee-icon-button.component';
import { PayeeIconComponent } from '../../payee-icon/payee-icon.component';
import { PayeePopupComponent } from '../../payee-popup/payee-popup.component';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { z } from 'zod';
import { startOfToday } from 'date-fns';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { MatDatepicker, MatDatepickerInput } from '@angular/material/datepicker';
import { PayeeInfoComponent } from '../../payee-info/payee-info.component';
import { CurrencyFlagUrlPipe } from '../../pipes/currency-flag-url/currency-flag-url.pipe';

const claimStatement = z.object({
  id: z.string(),
  label: z.string(),
  undisputed: z.number().min(0),
  disputed: z.number().min(0),
  payable: z.number().min(0),
  stated_paid: z.number().min(0),
  paid_before: z.number().min(0),
  currency: z.string().length(3),
});
type ClaimStatement = z.infer<typeof claimStatement>;

const principalClaim = claimStatement.extend({
  order: z.number().int().positive(),
  due_date: z.string().date().nullable(),
  optional_claims: z.array(claimStatement),
});

const dialogData = z.object({
  debtor_name: z.string().nullable(),
  receipt_date: z
    .string()
    .datetime({ offset: true })
    .transform((value) => new Date(value)),
  principal_claims: z.array(principalClaim),
  fee_claims: z.array(claimStatement),
});
type DialogData = z.infer<typeof dialogData>;

type PaymentForm = FormGroup<{
  currency: FormControl<string>;
  amount: FormControl<string>;
}>;

@Component({
  selector: 'app-hard-performance-claim-response',
  templateUrl: './hard-performance-claim-response.component.html',
  styleUrls: ['./hard-performance-claim-response.component.scss'],
  imports: [
    PayeePopupComponent,
    PayeeButtonComponent,
    MatDialogClose,
    ReactiveFormsModule,
    TranslateModule,
    CurrencyPipe,
    DecimalPipe,
    PayeeInfoComponent,
    PayeeIconButtonComponent,
    PayeeIconComponent,
    MatIcon,
    MatMenu,
    MatMenuTrigger,
    MatDatepicker,
    MatDatepickerInput,
    CurrencyFlagUrlPipe,
  ],
})
export class HardPerformanceClaimResponseComponent implements OnInit {
  private readonly dialogRef = inject<MatDialogRef<
    HardPerformanceClaimResponseComponent,
    RunTransition | undefined
  >>(MatDialogRef);
  private readonly data = inject<RunTransitionWithDialog>(MAT_DIALOG_DATA);
  private readonly workflowService = inject(WorkflowService);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(false);
  readonly dialogData = signal<DialogData | null>(null);
  readonly maxPaymentDate = startOfToday();

  readonly form = this.fb.group({
    payments: this.fb.array<PaymentForm>([]),
    paid_at: this.fb.control<Date | null>(null, {
      validators: [this.paidDateValidator()],
    }),
  });

  readonly formChange = toSignal(this.form.valueChanges, {
    initialValue: this.form.getRawValue(),
  });

  readonly currencies = computed(() => {
    const data = this.dialogData();
    if (!data) {
      return [];
    }

    return [...new Set(this.allClaims(data).map((claim) => claim.currency))];
  });

  readonly multiCurrency = computed(() => this.currencies().length > 1);

  readonly canAddPayment = computed(() => {
    this.formChange();
    return this.form.controls.payments.length < this.currencies().length;
  });

  readonly statementSummary = computed(() => {
    const data = this.dialogData();
    if (!data) {
      return null;
    }

    const totals = this.currencies().map((currency) => {
      const principals = data.principal_claims.filter((claim) => claim.currency === currency);
      const ancillary = [
        ...data.principal_claims.flatMap((claim) => claim.optional_claims),
        ...data.fee_claims,
      ].filter((claim) => claim.currency === currency);
      const principal = principals.reduce((sum, claim) => sum + claim.undisputed, 0);
      const ancillaryAmount = ancillary.reduce((sum, claim) => sum + claim.undisputed, 0);

      return {
        currency,
        principal,
        ancillary: ancillaryAmount,
      };
    });

    return {
      debtorName: data.debtor_name?.trim() || null,
      totals,
      hasAncillary: totals.some((total) => total.ancillary > 0),
    };
  });

  readonly hasPositivePayment = computed(() => {
    this.formChange();

    return this.totalAmount() > 0;
  });

  readonly askForPaymentDate = computed(() => {
    const data = this.dialogData();
    if (!data) {
      return false;
    }

    return this.allClaims(data).some((claim) => claim.paid_before > 0);
  });

  readonly hasOptionalClaims = computed(() => {
    const data = this.dialogData();
    if (!data) {
      return false;
    }

    return data.fee_claims.length > 0
      || data.principal_claims.some((claim) => claim.optional_claims.length > 0);
  });

  readonly optionalClaimsLocked = computed(() => {
    const data = this.dialogData();
    const value = this.formChange();
    if (!data || !this.askForPaymentDate()) {
      return false;
    }

    return this.isPaidBeforeReceipt(value?.paid_at, data.receipt_date);
  });

  readonly paymentCoversPrincipal = computed(() => {
    this.formChange();
    const summary = this.statementSummary();
    if (!summary) {
      return false;
    }

    return this.form.controls.payments.controls.some((payment) => {
      const principal = summary.totals
        .find((total) => total.currency === payment.controls.currency.value)
        ?.principal ?? 0;
      const amount = this.amountValue(payment.controls.amount.value);

      return amount > 0 && amount >= principal;
    });
  });

  constructor() {
    effect(() => {
      this.optionalClaimsLocked();
      for (const payment of this.form.controls.payments.controls) {
        payment.controls.amount.updateValueAndValidity({ emitEvent: false });
      }
      this.form.controls.paid_at.updateValueAndValidity({ emitEvent: false });
    });

    this.form.controls.payments.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.form.controls.paid_at.updateValueAndValidity({ emitEvent: false });
      });
  }

  async ngOnInit() {
    try {
      this.loading.set(true);
      const data = dialogData.parse(await this.workflowService.getDialogData({
        caseId: this.data.caseId,
        transitionId: this.data.transitionId,
      }));

      this.dialogData.set(data);

      const prefilled = this.currencies().filter((currency) => this.defaultAmount(currency) > 0);
      for (const currency of prefilled.length > 0 ? prefilled : this.currencies().slice(0, 1)) {
        this.form.controls.payments.push(this.createPayment(currency));
      }
      this.loading.set(false);
    } catch (error) {
      console.error('Error while loading dialog data', error);
      this.dialogRef.close();
    }
  }

  currencyOptions(index: number): string[] {
    const taken = this.form.controls.payments.controls
      .filter((_, i) => i !== index)
      .map((payment) => payment.controls.currency.value);

    return this.currencies().filter((currency) => !taken.includes(currency));
  }

  selectCurrency(payment: PaymentForm, currency: string, menu: MatMenuTrigger): void {
    payment.controls.currency.setValue(currency);
    payment.controls.currency.markAsDirty();
    menu.closeMenu();
  }

  addPayment(): void {
    const currency = this.currencyOptions(-1)[0];
    if (!currency) {
      return;
    }

    this.form.controls.payments.push(this.createPayment(currency));
  }

  revealDate(control: AbstractControl): void {
    control.markAsDirty();
    control.markAsTouched();
  }

  showDateError(control: AbstractControl): boolean {
    return (control.dirty || control.touched) && control.invalid;
  }

  removePayment(index: number): void {
    if (this.form.controls.payments.length <= 1) {
      return;
    }

    this.form.controls.payments.removeAt(index);
  }

  async submit() {
    if (this.form.invalid || !this.dialogData()) {
      for (const payment of this.form.controls.payments.controls) {
        payment.controls.amount.markAsDirty();
      }
      this.revealDate(this.form.controls.paid_at);
      return;
    }

    const raw = this.form.getRawValue();
    const closeValue: RunTransition = {
      caseId: this.data.caseId,
      transitionId: this.data.transitionId,
      params: {
        payments: raw.payments
          .map((payment) => ({
            currency: payment.currency,
            amount: this.amountValue(payment.amount),
          }))
          .filter((payment) => payment.amount > 0),
        paid_at: this.serializePaidAt(raw.paid_at),
      },
    };

    this.loading.set(true);
    try {
      await this.workflowService.runTransition(closeValue);
      this.dialogRef.close(closeValue);
    } catch (error) {
      this.workflowService.showTransitionError(error);
    } finally {
      this.loading.set(false);
    }
  }

  private createPayment(currency: string): PaymentForm {
    const payment: PaymentForm = this.fb.group({
      currency: this.fb.control(currency, { nonNullable: true }),
      amount: this.fb.control('', {
        nonNullable: true,
        validators: [
          this.formatAmountValidator(),
          this.maxPaidAmountValidator(),
        ],
      }),
    });

    payment.controls.amount.updateValueAndValidity({ emitEvent: false });
    payment.controls.currency.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => payment.controls.amount.updateValueAndValidity());

    return payment;
  }

  private totalAmount(): number {
    return this.form?.controls.payments.controls
      .reduce((sum, payment) => sum + this.amountValue(payment.controls.amount.value), 0) ?? 0;
  }

  private defaultAmount(currency: string): number {
    const data = this.dialogData();
    if (!data) {
      return 0;
    }

    return this.redeemableClaims(data, currency, false)
      .reduce((sum, claim) => sum + Math.min(claim.stated_paid, claim.payable), 0);
  }

  private maxPaidAmount(currency: string): number {
    const data = this.dialogData();
    if (!data) {
      return 0;
    }

    return this.redeemableClaims(data, currency, this.optionalClaimsLocked())
      .reduce((sum, claim) => sum + claim.payable, 0);
  }

  private redeemableClaims(data: DialogData, currency: string, principalsOnly: boolean): ClaimStatement[] {
    const claims: ClaimStatement[] = [];

    for (const principal of data.principal_claims) {
      if (principal.currency === currency) {
        claims.push(principal);
      }
      if (!principalsOnly) {
        claims.push(
          ...principal.optional_claims.filter((claim) => claim.currency === currency),
        );
      }
    }

    if (!principalsOnly) {
      claims.push(...data.fee_claims.filter((claim) => claim.currency === currency));
    }

    return claims;
  }

  private allClaims(data: DialogData): ClaimStatement[] {
    return [
      ...data.principal_claims,
      ...data.principal_claims.flatMap((principal) => principal.optional_claims),
      ...data.fee_claims,
    ];
  }

  private paidDateValidator(): ValidatorFn {
    return (control) => {
      if (
        !this.dialogData()
        || !this.askForPaymentDate()
        || this.totalAmount() <= 0
      ) {
        return null;
      }

      const value = control.value as Date | null;
      if (!value) {
        return { required: true };
      }

      if (
        !(value instanceof Date)
        || Number.isNaN(value.getTime())
        || value > this.maxPaymentDate
      ) {
        return { maxdate: true };
      }

      return null;
    };
  }

  private maxPaidAmountValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const currency = control.parent?.get('currency')?.value as string | undefined;
      if (!currency) {
        return null;
      }

      const amount = this.amountValue(control.value);
      const max = this.maxPaidAmount(currency);
      if (amount <= max) {
        return null;
      }

      return { max: { max, actual: amount } };
    };
  }

  private formatAmountValidator(): ValidatorFn {
    return (control) => {
      const value = control.value;
      if (value === null || value === undefined || value === '') {
        return null;
      }

      if (typeof value !== 'number' && typeof value !== 'string') {
        return { pattern: true };
      }

      const formattedValue = this.formatAmount(this.amountValue(value));
      if (formattedValue !== value) {
        setTimeout(() => control.patchValue(formattedValue));
      }

      return null;
    };
  }

  private formatAmount(value: number): string {
    return value.toLocaleString('hu-HU').replace(/,/g, ' ');
  }

  private serializePaidAt(paidAt: Date | null): string | null {
    if (!this.askForPaymentDate() || !paidAt || Number.isNaN(paidAt.getTime())) {
      return null;
    }

    const month = String(paidAt.getMonth() + 1).padStart(2, '0');
    const day = String(paidAt.getDate()).padStart(2, '0');

    return `${paidAt.getFullYear()}-${month}-${day}`;
  }

  private isPaidBeforeReceipt(paidAt: Date | null | undefined, receiptAt: Date): boolean {
    if (!paidAt || Number.isNaN(paidAt.getTime()) || Number.isNaN(receiptAt.getTime())) {
      return false;
    }

    const receipt = this.budapestParts(receiptAt);
    const paidDay = Date.UTC(paidAt.getFullYear(), paidAt.getMonth(), paidAt.getDate());
    const receiptDay = Date.UTC(receipt.year, receipt.month - 1, receipt.day);

    return paidDay < receiptDay;
  }

  private budapestParts(value: Date): { year: number; month: number; day: number } {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Europe/Budapest',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(value);
    const read = (type: Intl.DateTimeFormatPartTypes) => Number(
      parts.find((part) => part.type === type)?.value ?? '0',
    );

    return {
      year: read('year'),
      month: read('month'),
      day: read('day'),
    };
  }

  private amountValue(value: string | number | null | undefined): number {
    if (typeof value === 'number') {
      return value;
    }

    return parseInt(String(value ?? '').replace(/\D/g, '') || '0', 10);
  }
}
